import React, { useEffect, useState } from 'react';

const Rules = () => {
    const [formData, setFormData] = useState({
        quizTitle: "Champion's Arena",
        quizDescription: {
            english: "For true sports lovers! From cricket and football to Olympics – show off your sports smarts.",
            hindi: "सच्चे खेल प्रेमियों के लिए! क्रिकेट और फुटबॉल से लेकर ओलंपिक तक - अपनी खेल समझ दिखाएं।"
        },
        quizDuration: 24,
        timePerQuestion: 5,
        resultAnnouncement: 24,
        totalQuestions: 6,
        easyQuestions: 2,
        mediumQuestions: 2,
        hardQuestions: 2,
        totalCoinPool: 700,
        prizes: [
            { rank: "5th Place", coins: 500 },
            { rank: "2nd Place", coins: 200 }
        ],
        lifelinesAvailable: 2,
        playLimit: 2,
        quizType: "Daily",
        status: "Active",
        primaryColor: "#6849FF",
        secondaryColor: "#FFC800",
        scheduledDate: new Date().toISOString().split('T')[0],
        scheduledTime: "18:00",
        icons: "sport" // 'sport' or 'brain'
    });

    const [preview, setPreview] = useState(false);
    const [previewTab, setPreviewTab] = useState('visual'); // 'visual' or 'code'

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Handle nested objects
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData({
                ...formData,
                [parent]: {
                    ...formData[parent],
                    [child]: value
                }
            });
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    useEffect(() => {
        if (formData.quizType === 'Live') {
            // Convert from score-based to position-based ranking for Live quizzes
            const updatedPrizes = formData.prizes.map((prize, index) => {
                // If the rank is not already position-based, convert it
                if (!prize.rank.includes('Place')) {
                    const position = index + 1;
                    const suffix = getOrdinalSuffix(position);
                    return {
                        ...prize,
                        rank: `${position}${suffix} Place`
                    };
                }
                return prize;
            });

            setFormData(prev => ({
                ...prev,
                prizes: updatedPrizes
            }));
        } else if (formData.quizType === 'Daily') {
            // Convert from position-based to score-based ranking for Daily quizzes
            const updatedPrizes = formData.prizes.map((prize, index) => {
                // If the rank is not already score-based, convert it
                if (prize.rank.includes('Place')) {
                    // For Daily quizzes, we'll use score-based ranks
                    // Higher scores get higher rewards, so we'll start from the top
                    const scoreValue = formData.totalQuestions - index;
                    return {
                        ...prize,
                        rank: `${scoreValue} Correct Answers`
                    };
                }
                return prize;
            });

            setFormData(prev => ({
                ...prev,
                prizes: updatedPrizes
            }));
        }
    }, [formData.quizType]);

    // Helper function to get ordinal suffix
    const getOrdinalSuffix = (num) => {
        const j = num % 10;
        const k = num % 100;

        if (j === 1 && k !== 11) {
            return "st";
        }
        if (j === 2 && k !== 12) {
            return "nd";
        }
        if (j === 3 && k !== 13) {
            return "rd";
        }
        return "th";
    };


    const handlePrizeChange = (index, field, value) => {
        const updatedPrizes = [...formData.prizes];
        updatedPrizes[index] = {
            ...updatedPrizes[index],
            [field]: value
        };

        setFormData({
            ...formData,
            prizes: updatedPrizes
        });
    };

    const addPrize = () => {
        let newRank = "";

        // Create default rank based on quiz type
        if (formData.quizType === 'Live') {
            const position = formData.prizes.length + 1;
            const suffix = getOrdinalSuffix(position);
            newRank = `${position}${suffix} Place`;
        } else {
            // For Daily quizzes, use score-based ranks
            // Decrease by 1 for each new prize
            const scoreValue = Math.max(1, formData.totalQuestions - formData.prizes.length);
            newRank = `${scoreValue} Correct Answers`;
        }

        setFormData({
            ...formData,
            prizes: [...formData.prizes, { rank: newRank, coins: 0 }]
        });
    };

    const removePrize = (index) => {
        const updatedPrizes = [...formData.prizes];
        updatedPrizes.splice(index, 1);

        setFormData({
            ...formData,
            prizes: updatedPrizes
        });
    };

    const formatDateForDisplay = (dateString, timeString, language = 'english') => {
        try {
            const dateObj = new Date(`${dateString}T${timeString}`);

            if (language === 'english') {
                const options = {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                };

                return dateObj.toLocaleDateString('en-US', options);
            } else {
                // Hindi date format (simplified)
                const hindiMonths = ['जनवरी', 'फरवरी', 'मार्च', 'अप्रैल', 'मई', 'जून', 'जुलाई', 'अगस्त', 'सितंबर', 'अक्टूबर', 'नवंबर', 'दिसंबर'];
                const hindiDay = dateObj.getDate();
                const hindiMonth = hindiMonths[dateObj.getMonth()];
                const hindiYear = dateObj.getFullYear();
                const hindiHour = dateObj.getHours();
                const hindiMinute = dateObj.getMinutes();

                const hindiTime = `${hindiHour}:${hindiMinute < 10 ? '0' + hindiMinute : hindiMinute}`;
                return `${hindiDay} ${hindiMonth}, ${hindiYear} शाम ${hindiTime} बजे`;
            }
        } catch (e) {
            return language === 'english' ? 'Invalid date' : 'अमान्य तिथि';
        }
    };

    const getStatusTranslation = (status) => {
        const translations = {
            'Active': 'सक्रिय',
            'Inactive': 'निष्क्रिय',
            'Coming Soon': 'जल्द आ रहा है',
            'Finished': 'समाप्त'
        };

        return translations[status] || status;
    };
    // Get appropriate label text based on quiz type
    const getRankLabel = () => {
        if (formData.quizType === 'Live') {
            return "Rank";
        } else {
            return "Achievement";
        }
    };

    // Get appropriate placeholder text based on quiz type
    const getRankPlaceholder = () => {
        if (formData.quizType === 'Live') {
            return "e.g. 1st Place";
        } else {
            return "e.g. 5 Correct Answers";
        }
    };

    const generateHTML = () => {
        const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${formData.quizTitle} - Quiz Rules</title>
    <style>
        :root {
            --primary-color: ${formData.primaryColor};
            --secondary-color: ${formData.secondaryColor};
            --accent-color: ${formData.secondaryColor};
            --light-bg: #F9FAFE;
            --dark-text: #111723;
            --light-text: #FFFFFF;
            --border-color: #CFD1D3;
            --subfont-color: #888B91;
            --delete-color: #F34235;
            --th-color: #F2F0FF;
            --th-hover-color: #F5F8FF;
        }
        
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        
        body {
            background-color: var(--light-bg);
            color: var(--dark-text);
            line-height: 1.6;
        }
        
        .container {
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
        }
        
        .quiz-header {
            background-color: var(--primary-color);
            color: white;
            padding: 20px;
            border-radius: 10px 10px 0 0;
            position: relative;
            overflow: hidden;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .quiz-header::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(135deg, rgba(255, 200, 0, 0.3) 0%, rgba(255, 200, 0, 0) 50%);
            z-index: 1;
        }
        
        .quiz-header h1 {
            font-size: 2rem;
            margin: 0;
            position: relative;
            z-index: 2;
        }
        
        .quiz-header img {
            height: 60px;
            border-radius: 8px;
            position: relative;
            z-index: 2;
        }
        
        .language-toggle {
            background-color: var(--light-bg);
            padding: 10px;
            border-radius: 0;
            display: flex;
            justify-content: center;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            border-bottom: 1px solid var(--border-color);
        }
        
        .language-btn {
            background: none;
            border: none;
            padding: 8px 16px;
            margin: 0 5px;
            cursor: pointer;
            border-radius: 20px;
            font-weight: bold;
            transition: all 0.3s ease;
        }
        
        .language-btn.active {
            background-color: var(--primary-color);
            color: white;
        }
        
        .rules-container {
            background-color: white;
            border-radius: 0 0 10px 10px;
            padding: 20px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        
        .rules-section {
            display: none;
        }
        
        .rules-section.active {
            display: block;
            animation: fadeIn 0.5s ease;
        }
        
        @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        
        .rule-item {
            margin-bottom: 20px;
            border-left: 4px solid var(--primary-color);
            padding-left: 15px;
        }
        
        .rule-item h3 {
            color: var(--primary-color);
            margin-bottom: 8px;
            display: flex;
            align-items: center;
        }
        
        .rule-item h3 i {
            margin-right: 10px;
            font-size: 1.2rem;
        }
        
        .prize-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            background-color: var(--light-bg);
            border-radius: 8px;
            overflow: hidden;
        }
        
        .prize-table th, .prize-table td {
            padding: 12px;
            text-align: center;
            border-bottom: 1px solid var(--border-color);
        }
        
        .prize-table th {
            background-color: var(--primary-color);
            color: white;
        }
        
        .prize-table tr:nth-child(even) {
            background-color: var(--th-color);
        }
        
        .prize-table tr:hover {
            background-color: var(--th-hover-color);
        }
        
        .coin-value {
            color: var(--primary-color);
            font-weight: bold;
        }
        
        .${formData.icons === 'sport' ? 'sport' : 'brain'}-icons {
            display: flex;
            justify-content: center;
            margin: 20px 0;
        }
        
        .${formData.icons === 'sport' ? 'sport' : 'brain'}-icon {
            width: 40px;
            height: 40px;
            margin: 0 10px;
            background-color: var(--th-color);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
        }
        
        .highlight {
            font-weight: bold;
            color: var(--primary-color);
        }
        
        .footer {
            text-align: center;
            margin-top: 20px;
            color: var(--subfont-color);
            font-size: 0.9rem;
        }
        
        .status-badge {
            display: inline-block;
            padding: 5px 10px;
            border-radius: 12px;
            background-color: var(--delete-color);
            color: white;
            font-size: 0.9rem;
            font-weight: bold;
            margin-left: 10px;
        }
        
        .schedule-time {
            font-weight: bold;
            color: var(--primary-color);
            margin-top: 10px;
            display: block;
        }
        
        .countdown {
            background-color: var(--th-color);
            padding: 15px;
            border-radius: 8px;
            text-align: center;
            margin: 15px 0;
        }
        
        .countdown-value {
            font-size: 1.8rem;
            font-weight: bold;
            color: var(--primary-color);
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="quiz-header">
            <h1>${formData.quizTitle}</h1>
        </div>
        
        <div class="language-toggle">
            <button class="language-btn active" onclick="changeLanguage('english')">English</button>
            <button class="language-btn" onclick="changeLanguage('hindi')">हिंदी</button>
        </div>
        
        <div class="rules-container">
            <!-- English Rules -->
            <div id="english-rules" class="rules-section active">
                ${formData.icons === 'sport' ? `
                <div class="sport-icons">
                    <div class="sport-icon">⚽</div>
                    <div class="sport-icon">🏏</div>
                    <div class="sport-icon">🏀</div>
                    <div class="sport-icon">🎾</div>
                    <div class="sport-icon">🏊</div>
                </div>` : `
                <div class="brain-icons">
                    <div class="brain-icon">🧠</div>
                    <div class="brain-icon">💡</div>
                    <div class="brain-icon">🎯</div>
                    <div class="brain-icon">🏆</div>
                </div>`}
                
                <div class="rule-item">
                    <h3>📝 Quiz Description ${formData.quizType === 'Live' ? `<span class="status-badge">${formData.status}</span>` : ''}</h3>
                    <p>${formData.quizDescription.english}</p>
                    ${formData.quizType === 'Live' ? `<p>Quiz Type: <span class="highlight">Live</span></p>
                    <p class="schedule-time">Scheduled Time: ${formatDateForDisplay(formData.scheduledDate, formData.scheduledTime)}</p>` : ''}
                </div>
                
                <div class="rule-item">
                    <h3>⏱️ Timing Rules</h3>
                    <p>- Quiz Duration: <span class="highlight">${formData.quizDuration} ${formData.quizType === 'Live' ? 'minutes' : 'hours'}</span>${formData.quizType === 'Daily' ? ` (${formData.quizDuration * 60} minutes)` : ''}</p>
                    <p>- Time per Question: <span class="highlight">${formData.timePerQuestion} seconds</span></p>
                    <p>- Result Announcement: After <span class="highlight">${formData.resultAnnouncement} ${formData.quizType === 'Live' ? 'minutes' : 'hours'}</span>${formData.quizType === 'Daily' ? ` (${formData.resultAnnouncement * 60} minutes)` : ''}</p>
                </div>
                
                <div class="rule-item">
                    <h3>❓ Question Details</h3>
                    <p>- Total Questions: <span class="highlight">${formData.totalQuestions}</span></p>
                    <p>- Difficulty Level Distribution:</p>
                    <ul style="list-style-type: none; margin-left: 20px;">
                        <li>• Easy: ${formData.easyQuestions} question${formData.easyQuestions !== 1 ? 's' : ''}</li>
                        <li>• Medium: ${formData.mediumQuestions} question${formData.mediumQuestions !== 1 ? 's' : ''}</li>
                        <li>• Hard: ${formData.hardQuestions} question${formData.hardQuestions !== 1 ? 's' : ''}</li>
                    </ul>
                </div>
                
                <div class="rule-item">
                    <h3>🏆 Rewards & Prizes</h3>
                    <p>- Total Coin Pool: <span class="highlight">${formData.totalCoinPool.toLocaleString()} coins</span></p>
                    <table class="prize-table">
                        <tr>
                            <th>Rank</th>
                            <th>Prize (Coins)</th>
                        </tr>
                        ${formData.prizes.map(prize => `
                        <tr>
                            <td>${prize.rank}</td>
                            <td class="coin-value">${parseInt(prize.coins).toLocaleString()} coins</td>
                        </tr>`).join('')}
                    </table>
                </div>
                
                <div class="rule-item">
                    <h3>🎮 Gameplay Rules</h3>
                    <p>- Lifelines Available: <span class="highlight">${formData.lifelinesAvailable}</span></p>
                    <p>- Play Limit: You can attempt this quiz <span class="highlight">${formData.playLimit} ${formData.playLimit === 1 ? 'time' : 'times'}${formData.playLimit === 1 ? ' only' : ''}</span></p>
                    <p>- Quiz Type: <span class="highlight">${formData.quizType}</span></p>
                    ${formData.quizType !== 'Live' ? `<p>- Status: <span class="highlight">${formData.status}</span></p>` : ''}
                </div>
            </div>
            
            <!-- Hindi Rules -->
            <div id="hindi-rules" class="rules-section">
                ${formData.icons === 'sport' ? `
                <div class="sport-icons">
                    <div class="sport-icon">⚽</div>
                    <div class="sport-icon">🏏</div>
                    <div class="sport-icon">🏀</div>
                    <div class="sport-icon">🎾</div>
                    <div class="sport-icon">🏊</div>
                </div>` : `
                <div class="brain-icons">
                    <div class="brain-icon">🧠</div>
                    <div class="brain-icon">💡</div>
                    <div class="brain-icon">🎯</div>
                    <div class="brain-icon">🏆</div>
                </div>`}
                
                <div class="rule-item">
                    <h3>📝 क्विज विवरण ${formData.quizType === 'Live' ? `<span class="status-badge">${getStatusTranslation(formData.status)}</span>` : ''}</h3>
                    <p>${formData.quizDescription.hindi}</p>
                    ${formData.quizType === 'Live' ? `<p>क्विज प्रकार: <span class="highlight">लाइव</span></p>
                    <p class="schedule-time">निर्धारित समय: ${formatDateForDisplay(formData.scheduledDate, formData.scheduledTime, 'hindi')}</p>` : ''}
                </div>
                
                <div class="rule-item">
                    <h3>⏱️ समय नियम</h3>
                    <p>- क्विज अवधि: <span class="highlight">${formData.quizDuration} ${formData.quizType === 'Live' ? 'मिनट' : 'घंटे'}</span>${formData.quizType === 'Daily' ? ` (${formData.quizDuration * 60} मिनट)` : ''}</p>
                    <p>- प्रति प्रश्न समय: <span class="highlight">${formData.timePerQuestion} सेकंड</span></p>
                    <p>- परिणाम घोषणा: <span class="highlight">${formData.resultAnnouncement} ${formData.quizType === 'Live' ? 'मिनट' : 'घंटे'}</span> के बाद${formData.quizType === 'Daily' ? ` (${formData.resultAnnouncement * 60} मिनट)` : ''}</p>
                </div>
                
                <div class="rule-item">
                    <h3>❓ प्रश्न विवरण</h3>
                    <p>- कुल प्रश्न: <span class="highlight">${formData.totalQuestions}</span></p>
                    <p>- कठिनाई स्तर वितरण:</p>
                    <ul style="list-style-type: none; margin-left: 20px;">
                        <li>• आसान: ${formData.easyQuestions} प्रश्न</li>
                        <li>• मध्यम: ${formData.mediumQuestions} प्रश्न</li>
                        <li>• कठिन: ${formData.hardQuestions} प्रश्न</li>
                    </ul>
                </div>
                
                <div class="rule-item">
                    <h3>🏆 पुरस्कार और इनाम</h3>
                    <p>- कुल सिक्का पूल: <span class="highlight">${formData.totalCoinPool.toLocaleString()} सिक्के</span></p>
                    <table class="prize-table">
                        <tr>
                            <th>रैंक</th>
                            <th>पुरस्कार (सिक्के)</th>
                        </tr>
                        ${formData.prizes.map(prize => `
                        <tr>
                            <td>${prize.rank
                .replace("1st Place", "पहला स्थान")
                .replace("2nd Place", "दूसरा स्थान")
                .replace("3rd Place", "तीसरा स्थान")
                .replace("4th Place", "चौथा स्थान")
                .replace("5th Place", "पांचवां स्थान")
                .replace("Place", "स्थान")
                .replace("st", "ला")
                .replace("nd", "रा")
                .replace("rd", "रा")
                .replace("th", "वां")}</td>
                            <td class="coin-value">${parseInt(prize.coins).toLocaleString()} सिक्के</td>
                        </tr>`).join('')}
                    </table>
                </div>
                
                <div class="rule-item">
                    <h3>🎮 गेमप्ले नियम</h3>
                    <p>- उपलब्ध लाइफलाइन: <span class="highlight">${formData.lifelinesAvailable}</span></p>
                    <p>- खेल सीमा: आप इस क्विज को ${formData.playLimit === 1 ? 'केवल ' : ''}<span class="highlight">${formData.playLimit} बार</span> प्रयास कर सकते हैं</p>
                    <p>- क्विज प्रकार: <span class="highlight">${formData.quizType === "Daily" ? "दैनिक" : formData.quizType === "Live" ? "लाइव" : formData.quizType === "Weekly" ? "साप्ताहिक" : formData.quizType === "Monthly" ? "मासिक" : formData.quizType}</span></p>
                    ${formData.quizType !== 'Live' ? `<p>- स्थिति: <span class="highlight">${getStatusTranslation(formData.status)}</span></p>` : ''}
                </div>
            </div>
        </div>
        
        <div class="footer">
            <p>© ${new Date().getFullYear()} ${formData.quizTitle}. All rights reserved.</p>
        </div>
    </div>
    
    <script>
        function changeLanguage(language) {
            // Hide all language sections
            document.querySelectorAll('.rules-section').forEach(section => {
                section.classList.remove('active');
            });
            
            // Show selected language section
            document.getElementById(language + '-rules').classList.add('active');
            
            // Update button states
            document.querySelectorAll('.language-btn').forEach(btn => {
                btn.classList.remove('active');
            });
            
            // Add active class to clicked button
            event.currentTarget.classList.add('active');
        }
        
        ${formData.quizType === 'Live' ? `
        // Format the date
        function formatDate() {
            const scheduledDate = new Date("${formData.scheduledDate}T${formData.scheduledTime}:00.000Z");
            
            const options = {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
            };
            
            const formattedDate = scheduledDate.toLocaleDateString('en-US', options);
            document.querySelector('#english-rules .schedule-time').textContent = "Scheduled Time: " + formattedDate;
            
            // Hindi date format (simplified)
            const hindiMonths = ['जनवरी', 'फरवरी', 'मार्च', 'अप्रैल', 'मई', 'जून', 'जुलाई', 'अगस्त', 'सितंबर', 'अक्टूबर', 'नवंबर', 'दिसंबर'];
            const hindiDay = scheduledDate.getDate();
            const hindiMonth = hindiMonths[scheduledDate.getMonth()];
            const hindiYear = scheduledDate.getFullYear();
            const hindiHour = scheduledDate.getHours();
            const hindiMinute = scheduledDate.getMinutes();
            
            const hindiTime = \`\${hindiHour}:\${hindiMinute < 10 ? '0' + hindiMinute : hindiMinute}\`;
            const hindiDate = \`\${hindiDay} \${hindiMonth}, \${hindiYear} शाम \${hindiTime} बजे\`;
            
            document.querySelector('#hindi-rules .schedule-time').textContent = "निर्धारित समय: " + hindiDate;
        }
        
        // Call the function when the page loads
        window.onload = formatDate;` : ''}
    </script>
</body>
</html>`;

        return html;
    };

    const downloadHTML = () => {
        const html = generateHTML();
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${formData.quizTitle.replace(/\s+/g, '-').toLowerCase()}-quiz-template.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const LivePreviewComponent = () => {
        // State for language toggle in the preview
        const [previewLanguage, setPreviewLanguage] = useState('english');

        return (
            <div className="bg-gray-50 p-6 rounded shadow-inner" style={{ maxWidth: '800px', margin: '0 auto' }}>
                <div style={{
                    backgroundColor: formData.primaryColor,
                    color: 'white',
                    padding: '20px',
                    borderRadius: '10px 10px 0 0',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div style={{
                        content: '',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: `linear-gradient(135deg, ${formData.secondaryColor}4D 0%, ${formData.secondaryColor}00 50%)`,
                        zIndex: 1
                    }}></div>
                    <h1 style={{ fontSize: '2rem', margin: 0, position: 'relative', zIndex: 2 }}>
                        {formData.quizTitle}
                    </h1>
                </div>

                <div style={{
                    backgroundColor: '#F9FAFE',
                    padding: '10px',
                    display: 'flex',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    borderBottom: '1px solid #CFD1D3'
                }}>
                    <button
                        onClick={() => setPreviewLanguage('english')}
                        style={{
                            background: previewLanguage === 'english' ? formData.primaryColor : 'none',
                            border: 'none',
                            padding: '8px 16px',
                            margin: '0 5px',
                            cursor: 'pointer',
                            borderRadius: '20px',
                            fontWeight: 'bold',
                            color: previewLanguage === 'english' ? 'white' : 'inherit',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        English
                    </button>
                    <button
                        onClick={() => setPreviewLanguage('hindi')}
                        style={{
                            background: previewLanguage === 'hindi' ? formData.primaryColor : 'none',
                            border: 'none',
                            padding: '8px 16px',
                            margin: '0 5px',
                            cursor: 'pointer',
                            borderRadius: '20px',
                            fontWeight: 'bold',
                            color: previewLanguage === 'hindi' ? 'white' : 'inherit',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        हिंदी
                    </button>
                </div>

                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '0 0 10px 10px',
                    padding: '20px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                    {previewLanguage === 'english' ? (
                        // English Rules
                        <div>
                            {formData.icons === 'sport' ? (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    margin: '20px 0'
                                }}>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        margin: '0 10px',
                                        backgroundColor: '#F2F0FF',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px'
                                    }}>⚽</div>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        margin: '0 10px',
                                        backgroundColor: '#F2F0FF',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px'
                                    }}>🏏</div>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        margin: '0 10px',
                                        backgroundColor: '#F2F0FF',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px'
                                    }}>🏀</div>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        margin: '0 10px',
                                        backgroundColor: '#F2F0FF',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px'
                                    }}>🎾</div>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        margin: '0 10px',
                                        backgroundColor: '#F2F0FF',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px'
                                    }}>🏊</div>
                                </div>
                            ) : (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    margin: '20px 0'
                                }}>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        margin: '0 10px',
                                        backgroundColor: '#F2F0FF',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px'
                                    }}>🧠</div>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        margin: '0 10px',
                                        backgroundColor: '#F2F0FF',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px'
                                    }}>💡</div>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        margin: '0 10px',
                                        backgroundColor: '#F2F0FF',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px'
                                    }}>🎯</div>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        margin: '0 10px',
                                        backgroundColor: '#F2F0FF',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px'
                                    }}>🏆</div>
                                </div>
                            )}

                            <div style={{
                                marginBottom: '20px',
                                borderLeft: `4px solid ${formData.primaryColor}`,
                                paddingLeft: '15px'
                            }}>
                                <h3 style={{
                                    color: formData.primaryColor,
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    📝 Quiz Description
                                    {formData.quizType === 'Live' && (
                                        <span style={{
                                            display: 'inline-block',
                                            padding: '5px 10px',
                                            borderRadius: '12px',
                                            backgroundColor: '#F34235',
                                            color: 'white',
                                            fontSize: '0.9rem',
                                            fontWeight: 'bold',
                                            marginLeft: '10px'
                                        }}>{formData.status}</span>
                                    )}
                                </h3>
                                <p>{formData.quizDescription.english}</p>
                                {formData.quizType === 'Live' && (
                                    <>
                                        <p>Quiz Type: <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>Live</span></p>
                                        <p style={{
                                            fontWeight: 'bold',
                                            color: formData.primaryColor,
                                            marginTop: '10px'
                                        }}>Scheduled Time: {formatDateForDisplay(formData.scheduledDate, formData.scheduledTime)}</p>
                                    </>
                                )}
                            </div>

                            <div style={{
                                marginBottom: '20px',
                                borderLeft: `4px solid ${formData.primaryColor}`,
                                paddingLeft: '15px'
                            }}>
                                <h3 style={{
                                    color: formData.primaryColor,
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>⏱️ Timing Rules</h3>
                                <p>- Quiz Duration: <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>
                                    {formData.quizDuration} {formData.quizType === 'Live' ? 'minutes' : 'hours'}
                                </span>{formData.quizType === 'Daily' ? ` (${formData.quizDuration * 60} minutes)` : ''}</p>
                                <p>- Time per Question: <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>{formData.timePerQuestion} seconds</span></p>
                                <p>- Result Announcement: After <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>
                                    {formData.resultAnnouncement} {formData.quizType === 'Live' ? 'minutes' : 'hours'}
                                </span>{formData.quizType === 'Daily' ? ` (${formData.resultAnnouncement * 60} minutes)` : ''}</p>
                            </div>

                            <div style={{
                                marginBottom: '20px',
                                borderLeft: `4px solid ${formData.primaryColor}`,
                                paddingLeft: '15px'
                            }}>
                                <h3 style={{
                                    color: formData.primaryColor,
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>❓ Question Details</h3>
                                <p>- Total Questions: <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>{formData.totalQuestions}</span></p>
                                <p>- Difficulty Level Distribution:</p>
                                <ul style={{ listStyleType: 'none', marginLeft: '20px' }}>
                                    <li>• Easy: {formData.easyQuestions} question{formData.easyQuestions !== 1 ? 's' : ''}</li>
                                    <li>• Medium: {formData.mediumQuestions} question{formData.mediumQuestions !== 1 ? 's' : ''}</li>
                                    <li>• Hard: {formData.hardQuestions} question{formData.hardQuestions !== 1 ? 's' : ''}</li>
                                </ul>
                            </div>

                            <div style={{
                                marginBottom: '20px',
                                borderLeft: `4px solid ${formData.primaryColor}`,
                                paddingLeft: '15px'
                            }}>
                                <h3 style={{
                                    color: formData.primaryColor,
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>🏆 Rewards & Prizes</h3>
                                <p>- Total Coin Pool: <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>{formData.totalCoinPool.toLocaleString()} coins</span></p>
                                <table style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    marginTop: '10px',
                                    backgroundColor: '#F9FAFE',
                                    borderRadius: '8px',
                                    overflow: 'hidden'
                                }}>
                                    <thead>
                                        <tr>
                                            <th style={{
                                                padding: '12px',
                                                textAlign: 'center',
                                                borderBottom: '1px solid #CFD1D3',
                                                backgroundColor: formData.primaryColor,
                                                color: 'white'
                                            }}>Rank</th>
                                            <th style={{
                                                padding: '12px',
                                                textAlign: 'center',
                                                borderBottom: '1px solid #CFD1D3',
                                                backgroundColor: formData.primaryColor,
                                                color: 'white'
                                            }}>Prize (Coins)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {formData.prizes.map((prize, index) => (
                                            <tr key={index} style={{
                                                backgroundColor: index % 2 === 1 ? '#F2F0FF' : 'transparent'
                                            }}>
                                                <td style={{
                                                    padding: '12px',
                                                    textAlign: 'center',
                                                    borderBottom: '1px solid #CFD1D3'
                                                }}>{prize.rank}</td>
                                                <td style={{
                                                    padding: '12px',
                                                    textAlign: 'center',
                                                    borderBottom: '1px solid #CFD1D3',
                                                    color: formData.primaryColor,
                                                    fontWeight: 'bold'
                                                }}>{parseInt(prize.coins).toLocaleString()} coins</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div style={{
                                marginBottom: '20px',
                                borderLeft: `4px solid ${formData.primaryColor}`,
                                paddingLeft: '15px'
                            }}>
                                <h3 style={{
                                    color: formData.primaryColor,
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>🎮 Gameplay Rules</h3>
                                <p>- Lifelines Available: <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>{formData.lifelinesAvailable}</span></p>
                                <p>- Play Limit: You can attempt this quiz <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>
                                    {formData.playLimit} {formData.playLimit === 1 ? 'time' : 'times'}{formData.playLimit === 1 ? ' only' : ''}
                                </span></p>
                                <p>- Quiz Type: <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>{formData.quizType}</span></p>
                                {formData.quizType !== 'Live' && (
                                    <p>- Status: <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>{formData.status}</span></p>
                                )}
                            </div>
                        </div>
                    ) : (
                        // Hindi Rules
                        <div>
                            {formData.icons === 'sport' ? (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    margin: '20px 0'
                                }}>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        margin: '0 10px',
                                        backgroundColor: '#F2F0FF',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px'
                                    }}>⚽</div>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        margin: '0 10px',
                                        backgroundColor: '#F2F0FF',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px'
                                    }}>🏏</div>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        margin: '0 10px',
                                        backgroundColor: '#F2F0FF',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px'
                                    }}>🏀</div>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        margin: '0 10px',
                                        backgroundColor: '#F2F0FF',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px'
                                    }}>🎾</div>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        margin: '0 10px',
                                        backgroundColor: '#F2F0FF',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px'
                                    }}>🏊</div>
                                </div>
                            ) : (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    margin: '20px 0'
                                }}>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        margin: '0 10px',
                                        backgroundColor: '#F2F0FF',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px'
                                    }}>🧠</div>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        margin: '0 10px',
                                        backgroundColor: '#F2F0FF',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px'
                                    }}>💡</div>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        margin: '0 10px',
                                        backgroundColor: '#F2F0FF',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px'
                                    }}>🎯</div>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        margin: '0 10px',
                                        backgroundColor: '#F2F0FF',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px'
                                    }}>🏆</div>
                                </div>
                            )}

                            <div style={{
                                marginBottom: '20px',
                                borderLeft: `4px solid ${formData.primaryColor}`,
                                paddingLeft: '15px'
                            }}>
                                <h3 style={{
                                    color: formData.primaryColor,
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    📝 क्विज विवरण
                                    {formData.quizType === 'Live' && (
                                        <span style={{
                                            display: 'inline-block',
                                            padding: '5px 10px',
                                            borderRadius: '12px',
                                            backgroundColor: '#F34235',
                                            color: 'white',
                                            fontSize: '0.9rem',
                                            fontWeight: 'bold',
                                            marginLeft: '10px'
                                        }}>{getStatusTranslation(formData.status)}</span>
                                    )}
                                </h3>
                                <p>{formData.quizDescription.hindi}</p>
                                {formData.quizType === 'Live' && (
                                    <>
                                        <p>क्विज प्रकार: <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>लाइव</span></p>
                                        <p style={{
                                            fontWeight: 'bold',
                                            color: formData.primaryColor,
                                            marginTop: '10px'
                                        }}>निर्धारित समय: {formatDateForDisplay(formData.scheduledDate, formData.scheduledTime, 'hindi')}</p>
                                    </>
                                )}
                            </div>

                            <div style={{
                                marginBottom: '20px',
                                borderLeft: `4px solid ${formData.primaryColor}`,
                                paddingLeft: '15px'
                            }}>
                                <h3 style={{
                                    color: formData.primaryColor,
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>⏱️ समय नियम</h3>
                                <p>- क्विज अवधि: <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>
                                    {formData.quizDuration} {formData.quizType === 'Live' ? 'मिनट' : 'घंटे'}
                                </span>{formData.quizType === 'Daily' ? ` (${formData.quizDuration * 60} मिनट)` : ''}</p>
                                <p>- प्रति प्रश्न समय: <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>{formData.timePerQuestion} सेकंड</span></p>
                                <p>- परिणाम घोषणा: <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>
                                    {formData.resultAnnouncement} {formData.quizType === 'Live' ? 'मिनट' : 'घंटे'}
                                </span> के बाद{formData.quizType === 'Daily' ? ` (${formData.resultAnnouncement * 60} मिनट)` : ''}</p>
                            </div>

                            <div style={{
                                marginBottom: '20px',
                                borderLeft: `4px solid ${formData.primaryColor}`,
                                paddingLeft: '15px'
                            }}>
                                <h3 style={{
                                    color: formData.primaryColor,
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>❓ प्रश्न विवरण</h3>
                                <p>- कुल प्रश्न: <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>{formData.totalQuestions}</span></p>
                                <p>- कठिनाई स्तर वितरण:</p>
                                <ul style={{ listStyleType: 'none', marginLeft: '20px' }}>
                                    <li>• आसान: {formData.easyQuestions} प्रश्न</li>
                                    <li>• मध्यम: {formData.mediumQuestions} प्रश्न</li>
                                    <li>• कठिन: {formData.hardQuestions} प्रश्न</li>
                                </ul>
                            </div>

                            <div style={{
                                marginBottom: '20px',
                                borderLeft: `4px solid ${formData.primaryColor}`,
                                paddingLeft: '15px'
                            }}>
                                <h3 style={{
                                    color: formData.primaryColor,
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>🏆 पुरस्कार और इनाम</h3>
                                <p>- कुल सिक्का पूल: <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>{formData.totalCoinPool.toLocaleString()} सिक्के</span></p>
                                <table style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    marginTop: '10px',
                                    backgroundColor: '#F9FAFE',
                                    borderRadius: '8px',
                                    overflow: 'hidden'
                                }}>
                                    <thead>
                                        <tr>
                                            <th style={{
                                                padding: '12px',
                                                textAlign: 'center',
                                                borderBottom: '1px solid #CFD1D3',
                                                backgroundColor: formData.primaryColor,
                                                color: 'white'
                                            }}>रैंक</th>
                                            <th style={{
                                                padding: '12px',
                                                textAlign: 'center',
                                                borderBottom: '1px solid #CFD1D3',
                                                backgroundColor: formData.primaryColor,
                                                color: 'white'
                                            }}>पुरस्कार (सिक्के)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {formData.prizes.map((prize, index) => (
                                            <tr key={index} style={{
                                                backgroundColor: index % 2 === 1 ? '#F2F0FF' : 'transparent'
                                            }}>
                                                <td style={{
                                                    padding: '12px',
                                                    textAlign: 'center',
                                                    borderBottom: '1px solid #CFD1D3'
                                                }}>{prize.rank
                                                    .replace("1st Place", "पहला स्थान")
                                                    .replace("2nd Place", "दूसरा स्थान")
                                                    .replace("3rd Place", "तीसरा स्थान")
                                                    .replace("4th Place", "चौथा स्थान")
                                                    .replace("5th Place", "पांचवां स्थान")
                                                    .replace("Place", "स्थान")
                                                    .replace("st", "ला")
                                                    .replace("nd", "रा")
                                                    .replace("rd", "रा")
                                                    .replace("th", "वां")}</td>
                                                <td style={{
                                                    padding: '12px',
                                                    textAlign: 'center',
                                                    borderBottom: '1px solid #CFD1D3',
                                                    color: formData.primaryColor,
                                                    fontWeight: 'bold'
                                                }}>{parseInt(prize.coins).toLocaleString()} सिक्के</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div style={{
                                marginBottom: '20px',
                                borderLeft: `4px solid ${formData.primaryColor}`,
                                paddingLeft: '15px'
                            }}>
                                <h3 style={{
                                    color: formData.primaryColor,
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>🎮 गेमप्ले नियम</h3>
                                <p>- उपलब्ध लाइफलाइन: <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>{formData.lifelinesAvailable}</span></p>
                                <p>- खेल सीमा: आप इस क्विज को {formData.playLimit === 1 ? 'केवल ' : ''}<span style={{ fontWeight: 'bold', color: formData.primaryColor }}>{formData.playLimit} बार</span> प्रयास कर सकते हैं</p>
                                <p>- क्विज प्रकार: <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>
                                    {formData.quizType === "Daily" ? "दैनिक" :
                                        formData.quizType === "Live" ? "लाइव" :
                                            formData.quizType === "Weekly" ? "साप्ताहिक" :
                                                formData.quizType === "Monthly" ? "मासिक" :
                                                    formData.quizType}
                                </span></p>
                                {formData.quizType !== 'Live' && (
                                    <p>- स्थिति: <span style={{ fontWeight: 'bold', color: formData.primaryColor }}>{getStatusTranslation(formData.status)}</span></p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div style={{
                    textAlign: 'center',
                    marginTop: '20px',
                    color: '#888B91',
                    fontSize: '0.9rem'
                }}>
                    <p>© {new Date().getFullYear()} {formData.quizTitle}. All rights reserved.</p>
                </div>
            </div>
        );
    };

    return (
        <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
            <div className="flex items-center justify-between p-4 mt-4 bg-purple-700 text-white">
                <h1 className="text-xl font-bold">Champion's Arena Quiz Template Generator</h1>
                <div>
                    <button
                        onClick={() => setPreview(!preview)}
                        className="px-4 py-2 mr-2 bg-purple-600 hover:bg-purple-800 rounded text-white"
                    >
                        {preview ? "Edit Template" : "Preview Template"}
                    </button>
                    <button
                        onClick={downloadHTML}
                        className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 rounded text-white"
                    >
                        Download HTML
                    </button>
                </div>
            </div>

            {preview ? (
                <div className="p-4">
                    <div className="mb-4 flex border rounded overflow-hidden">
                        <button
                            onClick={() => setPreviewTab('visual')}
                            className={`flex-1 py-3 px-4 ${previewTab === 'visual' ? 'bg-purple-100 text-purple-700 font-bold' : 'bg-white'}`}
                        >
                            Visual Preview
                        </button>
                        <button
                            onClick={() => setPreviewTab('code')}
                            className={`flex-1 py-3 px-4 ${previewTab === 'code' ? 'bg-purple-100 text-purple-700 font-bold' : 'bg-white'}`}
                        >
                            HTML Code
                        </button>
                    </div>

                    {previewTab === 'visual' ? (
                        <div className="bg-white rounded shadow p-4 overflow-auto">
                            <LivePreviewComponent />
                        </div>
                    ) : (
                        <div className="bg-white rounded shadow p-4">
                            <h2 className="text-lg font-bold mb-2">HTML Code</h2>
                            <pre className="bg-gray-100 p-4 rounded overflow-auto max-h-[70vh] text-sm">
                                {generateHTML()}
                            </pre>
                        </div>
                    )}

                    <div className="flex justify-end mt-4">
                        <button
                            onClick={downloadHTML}
                            className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 rounded text-white font-bold"
                        >
                            Download HTML
                        </button>
                    </div>
                </div>
            ) : (
                <div className="p-4">
                    <div className="bg-white rounded shadow p-4 mb-4">
                        <h2 className="text-lg font-bold mb-4">Basic Information</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Quiz Title</label>
                                <input
                                    type="text"
                                    name="quizTitle"
                                    value={formData.quizTitle}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Quiz Type</label>
                                <select
                                    name="quizType"
                                    value={formData.quizType}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                >
                                    <option value="Daily">Daily</option>
                                    <option value="Live">Live</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                >
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                    <option value="Coming Soon">Coming Soon</option>
                                    <option value="Finished">Finished</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Icon Style</label>
                                <select
                                    name="icons"
                                    value={formData.icons}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                >
                                    <option value="sport">Sports Icons (⚽ 🏏 🏀 🎾 🏊)</option>
                                    <option value="brain">Brain/Quiz Icons (🧠 💡 🎯 🏆)</option>
                                </select>
                            </div>

                            {formData.quizType === 'Live' && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date</label>
                                        <input
                                            type="date"
                                            name="scheduledDate"
                                            value={formData.scheduledDate}
                                            onChange={handleInputChange}
                                            className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Time</label>
                                        <input
                                            type="time"
                                            name="scheduledTime"
                                            value={formData.scheduledTime}
                                            onChange={handleInputChange}
                                            className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                        />
                                    </div>
                                </>
                            )}

                            <div className="flex gap-4 md:col-span-2">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
                                    <div className="flex items-center">
                                        <input
                                            type="color"
                                            name="primaryColor"
                                            value={formData.primaryColor}
                                            onChange={handleInputChange}
                                            className="w-10 h-10 border-0 p-0 mr-2"
                                        />
                                        <input
                                            type="text"
                                            name="primaryColor"
                                            value={formData.primaryColor}
                                            onChange={handleInputChange}
                                            className="flex-1 p-2 border rounded"
                                        />
                                    </div>
                                </div>

                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
                                    <div className="flex items-center">
                                        <input
                                            type="color"
                                            name="secondaryColor"
                                            value={formData.secondaryColor}
                                            onChange={handleInputChange}
                                            className="w-10 h-10 border-0 p-0 mr-2"
                                        />
                                        <input
                                            type="text"
                                            name="secondaryColor"
                                            value={formData.secondaryColor}
                                            onChange={handleInputChange}
                                            className="flex-1 p-2 border rounded"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded shadow p-4 mb-4">
                        <h2 className="text-lg font-bold mb-4">Quiz Description</h2>
                        <div className="grid grid-cols-1 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">English Description</label>
                                <textarea
                                    name="quizDescription.english"
                                    value={formData.quizDescription.english}
                                    onChange={handleInputChange}
                                    rows="2"
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                ></textarea>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Hindi Description</label>
                                <textarea
                                    name="quizDescription.hindi"
                                    value={formData.quizDescription.hindi}
                                    onChange={handleInputChange}
                                    rows="2"
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded shadow p-4 mb-4">
                        <h2 className="text-lg font-bold mb-4">Timing Rules</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Quiz Duration ({formData.quizType === 'Live' ? 'minutes' : 'hours'})
                                </label>
                                <input
                                    type="number"
                                    name="quizDuration"
                                    value={formData.quizDuration}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Time per Question (seconds)</label>
                                <input
                                    type="number"
                                    name="timePerQuestion"
                                    value={formData.timePerQuestion}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Result Announcement ({formData.quizType === 'Live' ? 'minutes' : 'hours'})
                                </label>
                                <input
                                    type="number"
                                    name="resultAnnouncement"
                                    value={formData.resultAnnouncement}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded shadow p-4 mb-4">
                        <h2 className="text-lg font-bold mb-4">Question Details</h2>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Total Questions</label>
                                <input
                                    type="number"
                                    name="totalQuestions"
                                    value={formData.totalQuestions}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Easy Questions</label>
                                <input
                                    type="number"
                                    name="easyQuestions"
                                    value={formData.easyQuestions}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Medium Questions</label>
                                <input
                                    type="number"
                                    name="mediumQuestions"
                                    value={formData.mediumQuestions}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Hard Questions</label>
                                <input
                                    type="number"
                                    name="hardQuestions"
                                    value={formData.hardQuestions}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded shadow p-4 mb-4">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-bold">Rewards & Prizes</h2>
                            <button
                                onClick={addPrize}
                                className="px-3 py-1 bg-purple-600 hover:bg-purple-800 rounded text-white text-sm"
                            >
                                Add Prize
                            </button>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Total Coin Pool</label>
                            <input
                                type="number"
                                name="totalCoinPool"
                                value={formData.totalCoinPool}
                                onChange={handleInputChange}
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                        </div>

                        <div className="space-y-3">
                            {formData.prizes.map((prize, index) => (
                                <div key={index} className="flex items-center gap-4">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {getRankLabel()}
                                        </label>
                                        <input
                                            type="text"
                                            value={prize.rank}
                                            onChange={(e) => handlePrizeChange(index, 'rank', e.target.value)}
                                            className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                            placeholder={getRankPlaceholder()}
                                        />
                                    </div>

                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Coins</label>
                                        <input
                                            type="number"
                                            value={prize.coins}
                                            onChange={(e) => handlePrizeChange(index, 'coins', parseInt(e.target.value) || 0)}
                                            className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                        />
                                    </div>

                                    <button
                                        onClick={() => removePrize(index)}
                                        className="mt-6 p-2 text-red-500 hover:bg-red-50 rounded"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M3 6h18"></path>
                                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                                            <line x1="10" y1="11" x2="10" y2="17"></line>
                                            <line x1="14" y1="11" x2="14" y2="17"></line>
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Info about rank types */}
                        <div className="mt-3 text-sm text-gray-600 p-2 bg-blue-50 rounded border border-blue-200">
                            <p className="font-medium mb-1">
                                {formData.quizType === 'Live'
                                    ? "🏆 Live quizzes use position-based rankings (1st Place, 2nd Place, etc.)"
                                    : "🎯 Daily quizzes use score-based rankings (5 Correct Answers, etc.)"}
                            </p>
                            <p>
                                {formData.quizType === 'Live'
                                    ? "Players will be ranked by their performance in the live event."
                                    : "Players will receive coins based on their number of correct answers."}
                            </p>
                        </div>
                    </div>


                    <div className="bg-white rounded shadow p-4 mb-4">
                        <h2 className="text-lg font-bold mb-4">Gameplay Rules</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Lifelines Available</label>
                                <input
                                    type="number"
                                    name="lifelinesAvailable"
                                    value={formData.lifelinesAvailable}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Play Limit</label>
                                <input
                                    type="number"
                                    name="playLimit"
                                    value={formData.playLimit}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end mt-4">
                        <button
                            onClick={() => setPreview(true)}
                            className="px-6 py-3 bg-purple-600 hover:bg-purple-800 rounded text-white font-bold mr-3"
                        >
                            Preview Template
                        </button>
                        <button
                            onClick={downloadHTML}
                            className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 rounded text-white font-bold"
                        >
                            Generate & Download HTML
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Rules;